import React, { useState } from 'react';

import SimpleAggregation from 'components/AggregationCards/SimpleAggregation';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import RadioButton from 'components/RadioButton';
import SimpleTooltip from 'components/Tooltips/SimpleTooltip';
import GroupSelectUserContainer from 'containers/UserList';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { AdminUser } from 'models/adminUser';
import { GroupedListModel } from 'models/groupedList';
import { Scaffold } from 'screens/components/Group/styles';

import { Container, Title, Text, RadioButtonsContainer, Row, RadioButtonLabel } from './styles';

interface Props {
  target?: string;
  title: string;
  setFieldValue: any;
  formikValues: any;
  formikFieldName: string;
  userExclusion: AdminUser[];
  errorMessage?: string;
  onConfirmAuthorizationApproval: (value: string, formikFieldName: string) => void;
  onConfirmAuthorizationUsers: (value: GroupedListModel[], formikFieldName: string) => void;
}

const ApprovalLevel = ({
  title,
  formikValues,
  formikFieldName,
  onConfirmAuthorizationApproval,
  onConfirmAuthorizationUsers,
  userExclusion,
  errorMessage,
}: Props) => {
  const { t } = useStoreTranslation();
  const [showSelectUser, setShowSelectUsers] = useState(false);

  const handleAuthorizationApproval = (value: string) =>
    onConfirmAuthorizationApproval(value, `${formikFieldName}.requireApproval`);

  const authorizationValue = formikValues[formikFieldName];

  return (
    <Container>
      <Title>{title}</Title>
      <Text>{t('group.approvalMethod.selectApprovalLevel')}</Text>
      <RadioButtonsContainer>
        <Row>
          <RadioButton
            optionValue={authorizationValue.requireApproval}
            value="Always"
            handleChangeValue={handleAuthorizationApproval}
          />
          <RadioButtonLabel>{t('group.approvalMethod.alwaysRequired')}</RadioButtonLabel>
          <SimpleTooltip
            idTooltip={`tooltip-${Math.random().toString(36).substring(7)}`}
            text={t('group.tooltip.authorization.alwaysMandatory')}
          />
        </Row>
        <Row>
          <RadioButton
            optionValue={authorizationValue.requireApproval}
            value="OnlyIfRuleBreaks"
            handleChangeValue={handleAuthorizationApproval}
          />
          <RadioButtonLabel>{t('group.approvalMethod.onlyIfRuleBreaks')}</RadioButtonLabel>
          <SimpleTooltip
            idTooltip={`tooltip-${Math.random().toString(36).substring(7)}`}
            text={t('group.tooltip.authorization.onlyOut')}
          />
        </Row>
      </RadioButtonsContainer>

      <Text>{t('group.approvalMethod.addApprovalTitle')}</Text>
      <SimpleAggregation
        placeholder={t('group.approvalMethod.addApprovalPlaceholder')}
        title={t('group.approvalMethod.approvals', { count: authorizationValue.authorizationUsers.length })}
        icon={formikFieldName === 'authorizationPrimaryUsers' ? 'primary-approval' : 'secondary-approval'}
        active={authorizationValue.authorizationUsers.length > 0}
        badgeItems={authorizationValue.authorizationUsers}
        onAdd={() => setShowSelectUsers(true)}
        onClick={() => setShowSelectUsers(true)}
        errorMessage={errorMessage}
      />
      <ApplicationOverlayContent sidebar show={showSelectUser} onClose={() => setShowSelectUsers(false)}>
        <Scaffold>
          <GroupSelectUserContainer
            isModal
            isApprovalSelection
            title={t('group.groupAggregation.selectApproverTitle')}
            alreadySelected={authorizationValue.authorizationUsers}
            onConfirmSelection={(users: GroupedListModel[]) =>
              onConfirmAuthorizationUsers(users, `${formikFieldName}.authorizationUsers`)
            }
            selectableType="check"
            onCloseSelectionScreen={() => setShowSelectUsers(false)}
            userExclusion={userExclusion}
            buttonLabel={t('group.groupAggregation.addApprover')}
          />
        </Scaffold>
      </ApplicationOverlayContent>
    </Container>
  );
};

export default ApprovalLevel;
